import React, { useRef, useState } from "react";
import { 
	RCCButton, 
	RCCGrid, 
	RCCTooltip,  
	RCCDropDownItem, 
	RCCIcon,
	RCCInput,
	RCCBadge
} from "react-common-components";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import { useForm } from "react-hook-form";
import { useTranslation } from "src/context/Translations/context";
import Pagination from "src/components/Pagination";
import FiltersContainer from "src/components/FiltersContainer";
import PageTitle from "src/components/PageTitle";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import LovSectors from "src/components/Lov/Sectors";
import LovClassifications from "src/components/Lov/Classifications";
import LovUsers from "src/components/Lov/Users";
import { IRefIndicatorModal } from "src/components/ModalIndicator/interface";
import IndicatorModal from "src/components/ModalIndicator";
import LovMaps from "src/components/Lov/Maps";
import Select from "src/components/Select";
import AppIcons from "src/components/AppIcons";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { useAuth } from "src/context/Auth/context";
import LovPerspectives from "src/components/Lov/Perspectives";
import LovObjectives from "src/components/Lov/Objectives";
import DeleteModalIndicator from "./deleteModalIndicator";
import { IIndicatorListFilters, IRefDeleteIndicatorModal } from "./interface";

const Indicators: React.FC = () => { 
	const { translate } = useTranslation();
	const { administrator, superUser } = useAuth();
	const refModal = useRef<IRefIndicatorModal>(null);
	const refModalDelete = useRef<IRefDeleteIndicatorModal>(null);
	// Filters
	const [selectedMap, setSelectedMap] = useState("");
	const [selectedPerspective, setselectedPerspective] = useState("");
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<IIndicatorListFilters>({filter:"",situation:"A" });
	const { 
		handleSubmit : handleSubmitFilters, 
		register : registerFilters,
		control:controlFilters, 
		reset: resetFilters, 
		watch: watchFilters,
		setValue:setValueFilters 
	} = useForm();

	const submitFilters = (data: any) => {
		setFilters(() => {
			const newState = {} as IIndicatorListFilters;
			newState.filter = data.filter;
			newState.situation = data.situation.value;
			if(data.map)
				newState.map = typeof data.map === "object" ? data.map?.value : data.map;
			if(data.perspective)
				newState.perspective = typeof data.perspective === "object" ? data.perspective?.value : data.perspective;
			if(data.objective)
				newState.objective = typeof data.objective === "object" ? data.objective?.value : data.objective;
			if(data.classification)
				newState.classification = typeof data.classification === "object" ? data.classification?.value : data.classification;
			if(data.sector)
				newState.sector = typeof data.sector === "object" ? data.sector?.value : data.sector;
			if(data.responsible)
				newState.responsible = typeof data.responsible === "object" ? data.responsible?.value : data.responsible;

			return newState;
		});
		paginationRef.current?.reload();
	};

	const colSituacao = (row: any) => (
		<RCCBadge color={row.situation === "A" ? "green" : "red"}
			styleInline="font-size: 0.83rem;"
		>
			{row.situation === "A" ? translate("Ativo") : translate("Inativo")}
		</RCCBadge>
	);

	const areUsingFilter = ():boolean => !!filters.classification || !!filters.filter || !!filters.responsible || !!filters.sector || !!filters.map || !!filters.objective ||!!filters.perspective ||filters.situation !== "T";

	// List & Pagination
	const paginationRef = useRef<IForwardPagination>(null);
	const colOptions = (row: any) => 
		(administrator || superUser || row.permission >= 5) && (
			<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
				<OptionsColumn>
					<RCCDropDownItem
						selfEnclose
						onClick={() => { refModal?.current?.actionButtonChange(row.iqc001Id,false,() => paginationRef.current?.reload()); }}
					>
						<RCCIcon 
							name="pencil" 
						/>
				    {translate("Alterar")}
					</RCCDropDownItem>
				
					<RCCDropDownItem 
						selfEnclose
						onClick={() => { refModal?.current?.actionButtonChange(row.iqc001Id,true,() => paginationRef.current?.reload());}}
					>
						<RCCIcon 
							name="copy" 
						/>
						{translate("Copiar")}
					</RCCDropDownItem>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
						<RCCDropDownItem 
							selfEnclose
							onClick={() => { refModalDelete.current?.actionButtonDelete(row,() => paginationRef.current?.reload());}}
						>
							<RCCIcon 
								name="trash" 
							/>
				    {translate("Excluir")}
						</RCCDropDownItem>
					</CheckPermission>
				</OptionsColumn>
			</CheckPermission>
		)
	;

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
			>
				{/* Header -> Results + Buttons */}
				<PageTitle
					title={translate("Indicadores")}
					icon={() => <AppIcons name="indicators" />}
				>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						<RCCTooltip
							text={translate("Novo indicador")}
							color="purple"
						>
							<RCCButton
								icon={{name: "plus", color: "#FFF", size: 18}}
								color="purple"
								layout="isRounded"
								onClick={
									() => { refModal?.current?.actionButtonAdd(() => paginationRef.current?.reload());}
								}
								size="md"
							/>
						</RCCTooltip>
					</CheckPermission>
					<RCCTooltip
						text={translate("Filtros")}
						color={showFilters || areUsingFilter() ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={showFilters || areUsingFilter() ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
				</PageTitle>

				{/* Filters */}
				<FiltersContainer
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={
						() => resetFilters({
							filter: "",
							sector:{value:"",label:""},
							map:{value:"",label:""},
							objective:{value:"",label:""},
							perspective:{value:"",label:""},
							responsible:{value:"",label:""},
							classification:{value:"",label:""},
							situation:{value: "A", label: translate("Ativo")}
						})}
					showFilters={showFilters}
				>
					<RCCGrid
						container
						item
					>
						<RCCGrid
							container
							item
							direction="row"
							spacing={1}
						>
							<RCCGrid
								container 
								item
								xs={12}
								sm={4}
								md={3}
							>
								<LovSectors
									control={controlFilters}
									name="sector"
									label={translate("Setor")}
									isClearable	
								/> 
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={4}
								md={3}
							>
								<LovClassifications
									control={controlFilters}
									name="classification"
									label={translate("Classificação")}
									isClearable
								/> 
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={4}
								md={3}
							>
								<LovUsers
									control={controlFilters}
									name="responsible"
									label={translate("Responsável")}
									isClearable
								/>
							</RCCGrid>
							
							<RCCGrid
								container 
								item
								xs={12}
								sm={4}
								md={3}
							>
								<LovMaps 
									control={controlFilters}
									name="map"
									label={translate("Mapas")}
									defaultValue={filters.map}
									isClearable
									onChangeCapture={(e:any) => {
										setSelectedMap(e?.value||"");
										setselectedPerspective("");
										setValueFilters("perspective","");
									}}
								/>
							</RCCGrid>

							<RCCGrid
								container 
								item
								xs={12}
								sm={4}
								md={3}
							>
								<LovPerspectives
									key={selectedMap} 
									control={controlFilters}
									name="perspective"
									label={translate("Perspectivas")}
									defaultValue={filters.perspective}
									extraParametersOnLoad={{
										map:selectedMap
									}}
									onChangeCapture={(e:any) => {
										setselectedPerspective(e?.value||"");
										setValueFilters("objective","");
									}}
									cacheOptions={false}
									isClearable
								/>
							</RCCGrid>

							<RCCGrid
								container 
								item
								xs={12}
								sm={4}
								md={3}
							>
								<LovObjectives
									key={selectedPerspective}  
									control={controlFilters}
									name="objective"
									label={translate("Objetivo")}
									defaultValue={filters.objective}
									extraParametersOnLoad={{
										map:selectedMap,
										perspective:selectedPerspective
									}}
									cacheOptions={false}
									isClearable
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={4}
								md={3}
							>
								<Select 
									control={controlFilters}
									name="situation" 
									options={[
										{value: "A", label: translate("Ativo")}, 
										{value: "I", label: translate("Inativo")},
										{value: "T", label: translate("Todos")}
									]} 
									label={translate("Situação")}
									defaultValue={{value: "A", label: translate("Ativo")}}
									submit="value and label"
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={8}
								md={3}
							>
								<RCCInput
									name="filter" 
									label={translate("Pesquisar")}
									register={registerFilters}
									defaultValue={watchFilters("filter")}
								/>
							</RCCGrid>
						</RCCGrid>
					</RCCGrid>
				</FiltersContainer>

				{/* List */}
				<Pagination 
					ref={paginationRef}
					dataOrRoute="Indicators/List"
					filtersData={filters}    
					columns={[
						{
							idColumn: "iqc001Codigo",
							nameColumn: translate("Código"),
							sm: 1,
							md: 2,
							type: "string"
						},
						{
							idColumn: "iqc001Descricao",
							nameColumn: translate("Descrição"),
							sm: 3,
							md: 2,
							type: "string"
						},
						{
							idColumn: "taa001Descricao",
							nameColumn: translate("Setor"),
							sm: 2,
							md: 2,
							type: "string"
						},
						{
							idColumn: "taa002Descricao",
							nameColumn: translate("Classificação"),
							sm: 2,
							md: 2,
							type: "string"
						},
						{
							idColumn: "efa005Nome",
							nameColumn: translate("Responsável"),
							sm: 2,
							md: 2,
							type: "string"
						},
						{
							idColumn: "situation",
							nameColumn: translate("Situação"),
							sm: 1,
							md: 1,
							cellRenderer:colSituacao
						},
						{
							idColumn: "options",
							nameColumn: " ",
							justify: "right",
							sm: 1,
							md: 1,
							cellRenderer: colOptions,
							disableSort: true
						}
					]}
					styleInline={{
						ulContainer: "padding-top: 20px;"
					}}
				/>
			</RCCGrid>
			<IndicatorModal ref={refModal}/> 
			<DeleteModalIndicator ref={refModalDelete} />
		</>
	);
};

export default Indicators;
